import { ExportVideoDialogState } from "./ExportVideoDialog";
import {
  IconAlertCircle,
  IconDots,
  IconDownload,
  IconLink,
  IconVideo,
} from "@tabler/icons-react";
import { Button } from "@/lib/components/Button/Button";
import { Badge } from "@/lib/components/Badge";
import {
  VideoRenderDto,
  VideoRenderUpscaleQuality,
} from "@repo/api/video-render";
import { downloadFile, formatCreatedAt } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import { useSelfQuery } from "@/lib/queries/user-queries";
import { useParams } from "react-router";
import vimeoLogo from "./icons/vimeo-logo.png";
import { EXPORT_QUALITY_OPTIONS } from "./ExportQualityView";
import { DialogHeader, DialogTitle } from "@/lib/components/Dialog";
import LoadingSpinner from "@/lib/components/LoadingSpinner";

type Props = {
  setDialogState: (state: ExportVideoDialogState) => void;
  videoRenders: VideoRenderDto[] | undefined;
};
export default function EarlierExportsView({
  setDialogState,
  videoRenders,
}: Props) {
  if (!videoRenders) return null;

  const sortedVideoRenders = videoRenders.sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <>
      <DialogHeader
        variant="bordered"
        onBack={() => setDialogState({ view: "EXPORT" })}
        showCloseButton={true}
      >
        <DialogTitle>Earlier exports</DialogTitle>
      </DialogHeader>
      <div className="flex max-h-120 flex-1 flex-col gap-3 overflow-y-auto px-4 pt-5 pb-8 md:px-7">
        {sortedVideoRenders.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center">
            <p className="text-sm text-text-secondary">
              No earlier exports for this video
            </p>
          </div>
        )}

        {sortedVideoRenders.map((videoRender) => (
          <VideoRenderItem
            key={videoRender.externalId}
            videoRender={videoRender}
            setDialogState={setDialogState}
          />
        ))}
      </div>
    </>
  );
}

export function getQualityLabel(
  upscaleQuality: VideoRenderUpscaleQuality | null,
): string | null {
  const option = EXPORT_QUALITY_OPTIONS.find(
    (option) => option.quality === upscaleQuality,
  );

  if (!option) {
    return null;
  }

  let label = option.label;
  if (option.sublabel) {
    label += ` (${option.sublabel})`;
  }

  return label;
}

function VideoRenderItem({
  videoRender,
  setDialogState,
}: {
  videoRender: VideoRenderDto;
  setDialogState: (state: ExportVideoDialogState) => void;
}) {
  const { data: user } = useSelfQuery();
  const { teamExternalId } = useParams();

  if (!user || !teamExternalId) return null;

  let failedText = "Failed to export video";

  if (videoRender.upscaleQuality !== null) {
    failedText += ". Credits have been refunded.";
  }

  const metadata = [
    videoRender.aspectRatio,
    getQualityLabel(videoRender.upscaleQuality),
  ].filter((value): value is string => Boolean(value));

  const hasShareableLink = videoRender.shareInfo?.status === "done";

  return (
    <div className="flex flex-row items-center gap-4 rounded-lg border border-border px-4 py-2.5">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg">
        {["rendering", "pending", "upscaling"].includes(videoRender.status) && (
          <LoadingSpinner size={20} />
        )}
        {videoRender.status === "failed" && (
          <IconAlertCircle className="h-5 w-5 text-error" />
        )}
        {videoRender.status === "done" && (
          <IconVideo className="h-5 w-5 text-text-primary" />
        )}
      </div>
      <div className="flex flex-1 flex-col overflow-hidden">
        <p className="line-clamp-1 text-sm font-medium text-ellipsis">
          {videoRender.externalFileName
            ? `${videoRender.externalFileName}.mp4`
            : "Exported video"}
        </p>
        <div className="mt-px flex items-center gap-1.5">
          <p className="line-clamp-1 text-xs text-ellipsis text-text-secondary">
            {["rendering", "pending", "upscaling"].includes(
              videoRender.status,
            ) && "Rendering video..."}
            {videoRender.status === "failed" && failedText}
            {videoRender.status === "done" &&
              formatCreatedAt(videoRender.createdAt, "Exported")}
          </p>
          {metadata.map((label) => (
            <Badge key={label} variant="outline" size="xs">
              {label}
            </Badge>
          ))}
          {hasShareableLink && (
            <Badge variant="success" size="xs" showDot>
              Public
            </Badge>
          )}
        </div>
      </div>

      {videoRender.status === "done" && (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="iconSm"
            className="[&_svg]:size-5"
            onClick={() =>
              videoRender.downloadUrl && downloadFile(videoRender.downloadUrl)
            }
          >
            <IconDownload />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="iconXs" className="[&_svg]:size-5">
                <IconDots />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="min-w-50">
              <DropdownMenuItem
                onClick={() =>
                  setDialogState({
                    view: "SHARE_LINK",
                    videoRenderExternalId: videoRender.externalId,
                    fromView: "EARLIER_EXPORTS",
                  })
                }
              >
                <IconLink className="h-4 w-4" />
                <span>{hasShareableLink ? "Manage link" : "Share link"}</span>
              </DropdownMenuItem>

              {videoRender.vimeoInfo !== null && (
                <DropdownMenuItem
                  onClick={() =>
                    setDialogState({
                      view: "EXPORT_DETAILS",
                      videoRenderExternalId: videoRender.externalId,
                      exportType: "VIMEO",
                      fromView: "EARLIER_EXPORTS",
                    })
                  }
                >
                  <img src={vimeoLogo} className="h-4 w-4" />
                  <span>View Vimeo link</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
